import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Loader2, PowerOff } from 'lucide-react';
import { useTranslation, mapPublishError } from '@dukanti/shared';
import type { PublishErrorKey } from '@dukanti/shared';
import { useUnpublishStore } from '@/hooks/use-unpublish-store';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface UnpublishStoreDialogProps {
  /** Store to unpublish. Server re-checks admin membership. */
  readonly storeId: string;
  /** Controlled open state. */
  readonly open: boolean;
  /** Fires on open/close transitions from the AlertDialog primitive. */
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for taking a store offline (F-004 Decision 2).
 *
 * Visual tone is deliberately neutral — no red, no destructive
 * language — because unpublishing is fully reversible (Decision 1
 * leaves child `products.published` values untouched so a subsequent
 * re-publish is instant). Recoloring this dialog red would imply data
 * loss and discourage legitimate pauses.
 *
 * The primary action is **"Stay published"** (the reversal) rendered in
 * Za'atar Olive — the color of the happy path. The secondary action is
 * "Unpublish", rendered as a neutral ghost-with-border button. This
 * asymmetry is the UX nudge: if the merchant opened the dialog by
 * mistake, one clear button brings them back to the published state.
 *
 * Error handling mirrors `PublishStoreDialog` exactly:
 *   - `errorKey` state holds the mapped `PublishErrorKey`.
 *   - `mapPublishError` is the same code-first mapper used for publish,
 *     and handles the same 42501 / P0002 / network / unknown branches.
 *     `noStockedProducts` cannot occur on an unpublish path because the
 *     RPC skips the inventory gate when `publish = false`, so we still
 *     render a generic branch for it (dead-code but safe) — the test
 *     suite does not exercise it.
 *   - Inline banner + sonner toast on failure; the dialog stays open
 *     so the merchant can retry or cancel.
 *
 * Test-ids (F-004 design §5.5):
 *   - `unpublish-store-dialog`                  — root dialog
 *   - `unpublish-store-dialog-confirm`          — Unpublish button
 *   - `unpublish-store-dialog-stay`             — "Stay published" primary
 *   - `unpublish-store-dialog-error`            — any error banner
 *   - `unpublish-store-dialog-error-<key>`      — per-branch banner
 */
export function UnpublishStoreDialog({ storeId, open, onOpenChange }: UnpublishStoreDialogProps) {
  const { t, dir } = useTranslation();
  const unpublishMutation = useUnpublishStore();
  const [errorKey, setErrorKey] = useState<PublishErrorKey | null>(null);

  const isPending = unpublishMutation.isPending;

  useEffect(() => {
    if (!open) {
      setErrorKey(null);
    }
  }, [open]);

  function handleOpenChange(next: boolean): void {
    // Block closing while pending — same rationale as publish dialog:
    // the round-trip is short but non-zero, and a mid-flight close
    // would leave the merchant unsure whether the operation landed.
    if (isPending) return;
    if (!next) setErrorKey(null);
    onOpenChange(next);
  }

  function handleConfirm(): void {
    if (isPending) return;
    setErrorKey(null);
    unpublishMutation.mutate(
      { storeId },
      {
        onSuccess: () => {
          toast.success(t('admin.publishStore.toasts.unpublished'));
          onOpenChange(false);
        },
        onError: (err) => {
          const mapped = mapPublishError(err);
          setErrorKey(mapped);
          toast.error(t(mapped));
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent dir={dir} data-testid="unpublish-store-dialog" className="gap-0 p-8">
        {/* Icon wrap — neutral Sand background, Stone foreground. The
            pause-circle glyph reads as "paused", not "destroyed". */}
        <div
          aria-hidden="true"
          className="bg-sand-100 text-sand-500 mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
        >
          <PowerOff className="size-7" strokeWidth={1.75} />
        </div>

        <AlertDialogHeader className="mb-3">
          <AlertDialogTitle className="text-center">
            {t('admin.publishStore.unpublishModal.title')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription className="text-sand-600 mb-6 text-center text-sm leading-relaxed">
          {t('admin.publishStore.unpublishModal.description')}
        </AlertDialogDescription>

        {errorKey ? (
          <div
            role="alert"
            data-testid="unpublish-store-dialog-error"
            data-error-key={errorKey}
            className={cn(
              'border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card mb-6',
              'flex items-start gap-2 border p-3 text-start text-sm',
            )}
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <div className="flex-1">
              {errorKey === 'common.errors.permissionDenied' ? (
                <p data-testid="unpublish-store-dialog-error-permissionDenied">
                  {t('common.errors.permissionDenied')}
                </p>
              ) : errorKey === 'common.errors.storeNotFound' ? (
                <p data-testid="unpublish-store-dialog-error-storeNotFound">
                  {t('common.errors.storeNotFound')}
                </p>
              ) : errorKey === 'common.errors.networkFailed' ? (
                <p data-testid="unpublish-store-dialog-error-networkFailed">
                  {t('common.errors.networkFailed')}
                </p>
              ) : errorKey === 'admin.publishStore.errors.noStockedProducts' ? (
                // Dead-code branch for the unpublish path — the RPC
                // does not run the inventory gate when publish=false
                // — but rendered defensively so the banner never
                // crashes on an unexpected mapping outcome.
                /* v8 ignore next */
                <p data-testid="unpublish-store-dialog-error-noStockedProducts">
                  {t('admin.publishStore.errors.noStockedProducts')}
                </p>
              ) : (
                <p data-testid="unpublish-store-dialog-error-unknown">
                  {t('common.errors.unknown')}
                </p>
              )}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="flex-col-reverse gap-2 sm:flex-col-reverse sm:space-x-0">
          {/* Secondary: confirm unpublish — neutral ghost with Sand
              border. NOT destructive. */}
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid="unpublish-store-dialog-confirm"
            aria-busy={isPending || undefined}
            className="border-sand-200 text-sand-500 hover:bg-sand-100 hover:text-sand-700 w-full border"
          >
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                <span>{t('admin.publishStore.unpublishCtaLabelSubmitting')}</span>
              </>
            ) : (
              <>
                <PowerOff aria-hidden="true" className="size-4" strokeWidth={1.75} />
                <span>{t('admin.publishStore.unpublishModal.confirm')}</span>
              </>
            )}
          </Button>
          {/* Primary: stay published — Za'atar Olive. The reversal
              is the loud happy-path choice, not the destructive one. */}
          <Button
            type="button"
            variant="default"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            data-testid="unpublish-store-dialog-stay"
            className="w-full"
          >
            {t('common.cancel')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
